import { t } from 'ttag'
import styled from 'styled-components'
import { useMemo, useCallback } from 'react'

import { FilterGrid } from '@h2/common/components/filterGrid'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Field } from '@h2/components/inputs/field'
import { Flex } from '@h2/components/layout/flex'
import { gap } from '@h2/common/components/page'
import { Button } from '@h2/components/buttons/button'
import { Select } from '@h2/components/inputs/select'
import { Switch } from '@h2/components/inputs/switch'
import { WHITE } from '@h2/components/colors'

import { LessonSelect } from './lessonSelect'

const FlexField = styled(Field)`
  flex: 1;
`
const Form = styled.form`
  width: 100%;
`
const statusOptions = [
  { label: 'All Statuses', value: -1 },
  { label: 'Assigned', value: 0 },
  { label: 'Started', value: 1 },
  { label: 'Completed', value: 2 },
  { label: 'Approved', value: 3 }
]
const AssignmentFilterGrid = ({ routeColor, isFetching, filter }) => {
  const filterData = useMemo(() => {
    const { start, numPerPage, ...rest } = filter.getData[filter.key] ?? {}

    return rest
  }, [filter])
  const { register, isInvalid, handleOnSubmit, error, value } = useForm({
    initialValues: {
      ...filterData,
      status: statusOptions.find(so => so.value === filterData.status) || statusOptions[0]
    }
  })
  const updateFilters = useCallback(
    form => {
      const { status, lessonId, ...rest } = form
      const toSubmit = {
        ...filter.getData[filter.key],
        status,
        lessonId,
        ...rest
      }

      if (status) {
        toSubmit.status = status.value
      }

      if (lessonId) {
        toSubmit.lessonId = lessonId.value
      }

      filter.setData({
        ...filter.getData,
        [filter.key]: toSubmit
      })
    },
    [filter]
  )

  return (
    <FilterGrid bgColor={routeColor}>
      <Form onSubmit={handleOnSubmit(form => updateFilters(form))}>
        <Flex gap={gap}>
          <FlexField
            desc={error.status}
            htmlFor="statusOptions"
            status={error.status ? 'error' : 'default'}>
            <Select id="statusOptions" items={statusOptions} {...register('status')} />
          </FlexField>
          <FlexField
            desc={error.lessonId}
            htmlFor="lessonId"
            status={error.lessonId ? 'error' : 'default'}>
            <LessonSelect {...register('lessonId')} />
          </FlexField>
          <FlexField
            desc={error.userSelected}
            status={error.userSelected ? 'error' : 'default'}>
            <Switch
              defaultOn={value.userSelected}
              display="monochrome"
              labelPosition="right"
              color={WHITE}
              label={t`User Selected`}
              {...register('userSelected', { binary: true })}
            />
          </FlexField>
          <FlexField
            desc={error.assignedByCurrentUser}
            status={error.assignedByCurrentUser ? 'error' : 'default'}>
            <Switch
              defaultOn={value.assignedByCurrentUser}
              display="monochrome"
              labelPosition="right"
              color={WHITE}
              label={t`Assigned By Me`}
              {...register('assignedByCurrentUser', { binary: true })}
            />
          </FlexField>
          <FlexField
            desc={error.assignedByOtherUser}
            status={error.assignedByOtherUser ? 'error' : 'default'}>
            <Switch
              defaultOn={value.assignedByOtherUser}
              display="monochrome"
              labelPosition="right"
              color={WHITE}
              label={t`Assigned By Others`}
              {...register('assignedByOtherUser', { binary: true })}
            />
          </FlexField>
          <Button
            style={{ flex: '1' }}
            type="submit"
            align="center"
            isLoading={isFetching}
            isDisabled={isFetching || isInvalid}>
            {t`Update`}
          </Button>
        </Flex>
      </Form>
    </FilterGrid>
  )
}

export { AssignmentFilterGrid }
